/**
 * IndexedDB wrapper for per-user encrypted key storage.
 *
 * Each record is keyed by `userId` and contains:
 *   { userId, encPwd, encRec, publicJwk }
 *
 * encPwd — private key encrypted by the messaging password
 * encRec — private key encrypted by the recovery phrase
 * publicJwk — the raw public key JWK (not secret, cached locally for convenience)
 */

const DB_NAME    = 'bha-keys';
const DB_VERSION = 1;
const STORE      = 'keys';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(STORE, { keyPath: 'userId' });
    };

    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = ()  => reject(req.error);
  });
}

/**
 * Save key data for a user. Overwrites any existing record.
 * @param {string} userId
 * @param {{ encPwd: object, encRec: object, publicJwk: object }} data
 */
export async function saveKeys(userId, data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ userId, ...data });
    tx.oncomplete = resolve;
    tx.onerror    = () => reject(tx.error);
  });
}

/**
 * Load key data for a user. Returns null if not found.
 * @param {string} userId
 * @returns {Promise<{ userId, encPwd, encRec, publicJwk } | null>}
 */
export async function loadKeys(userId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(userId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror   = () => reject(req.error);
  });
}

/**
 * Delete key data for a user (used during key rotation).
 * @param {string} userId
 */
export async function clearKeys(userId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(userId);
    tx.oncomplete = resolve;
    tx.onerror    = () => reject(tx.error);
  });
}

/**
 * Returns true if a key record exists locally for this user.
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export async function hasLocalKey(userId) {
  const stored = await loadKeys(userId);
  return stored !== null;
}
